import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { orange } from "@mui/material/colors";

type MoveEventDialogContentProps = {
  isSameDate: boolean;
  isDifferentClinic: boolean;
  isDifferentStaffMember: boolean;
  prevStartDate: string;
  prevEndDate: string;
  prevStaffMemberFullName: string;
  newStaffMemberFullName: string;
  prevClinicName: string;
  newClinicName: string;
  newStartDate: string;
  newEndDate: string;
  hasRoomConflict?: boolean;
  roomName?: string;
};

export default function MoveEventDialogContent({
  isSameDate,
  isDifferentClinic,
  isDifferentStaffMember,
  prevStartDate,
  prevEndDate,
  prevStaffMemberFullName,
  newStaffMemberFullName,
  prevClinicName,
  newClinicName,
  newStartDate,
  newEndDate,
  hasRoomConflict,
  roomName
}: MoveEventDialogContentProps) {
  return (
    <Stack gap="6px">
      <Stack direction="row" sx={{ alignItems: "flex-start" }}>
        <Typography
          variant="body2"
          fontSize="20px"
          width="48px"
          lineHeight="28px"
        >
          From
        </Typography>
        <Stack sx={{ ml: "8px" }} gap={0.5}>
          <Typography
            component="span"
            variant="body2"
            fontSize="18px"
            fontWeight={isSameDate ? undefined : 700}
            lineHeight="28px"
          >
            {prevStartDate} - {prevEndDate}
          </Typography>
          {isDifferentStaffMember && (
            <Typography
              component="span"
              variant="body3"
              lineHeight="20px"
              fontSize="16px"
            >
              {prevStaffMemberFullName}
            </Typography>
          )}
          {isDifferentClinic && (
            <Typography
              component="span"
              variant="body3"
              lineHeight="20px"
              fontSize="16px"
            >
              {prevClinicName}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Stack direction="row">
        <Typography
          variant="body2"
          fontSize="20px"
          width="48px"
          lineHeight="28px"
        >
          To
        </Typography>
        <Stack sx={{ ml: "8px" }}>
          <Typography
            component="span"
            variant="body2"
            fontSize="18px"
            fontWeight={isSameDate ? undefined : 700}
            lineHeight="28px"
            sx={{ px: 0.25 }}
          >
            {newStartDate} - {newEndDate}
          </Typography>
          {isDifferentStaffMember && (
            <Typography
              component="span"
              variant="body3"
              lineHeight="20px"
              fontSize="16px"
              fontWeight={700}
              sx={{ px: 0.25 }}
            >
              {newStaffMemberFullName}
            </Typography>
          )}
          {isDifferentClinic && (
            <Typography
              component="span"
              variant="body3"
              lineHeight="20px"
              fontSize="16px"
              fontWeight={700}
              sx={{ px: 0.25 }}
            >
              {newClinicName}
            </Typography>
          )}
        </Stack>
      </Stack>

      {hasRoomConflict && roomName && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            mt: 2,
            p: 1.5,
            backgroundColor: orange[50],
            borderRadius: 1,
            border: `1px solid ${orange[200]}`
          }}
        >
          <WarningAmberIcon sx={{ color: orange[700], height: 20 }} />
          <Typography variant="body2" sx={{ color: orange[900] }}>
            Warning: Room "{roomName}" is already reserved at the new time.
            Moving this appointment will create a room conflict. Move anyway?
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
